/**
 * AiEnhancements — "polish", "translate", "suggest title" buttons grouped
 * into a small AI assist panel for the editor's left side.
 *
 * Each action is a separate mutation so errors + loading states don't bleed
 * across. The user can use any in any order once they have a transformed
 * meeting.
 */
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  AlertTriangle,
  AlignLeft,
  BookOpen,
  CalendarPlus,
  Languages,
  Lightbulb,
  ListChecks,
  Mic,
  RotateCcw,
  ScanSearch,
  Sliders,
  Wand2,
} from 'lucide-react';
import { api, ApiError } from '../../lib/apiClient.ts';
import { NO_AUTOFILL } from '../../lib/noAutofill.ts';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '../ui/Modal.tsx';
import { ExtractActionItemsDialog } from './ExtractActionItemsDialog.tsx';
import { AmbiguityResolverDialog } from './AmbiguityResolverDialog.tsx';
import { ConsistencyCheckDialog } from './ConsistencyCheckDialog.tsx';
import { GlossaryHarvesterDialog } from './GlossaryHarvesterDialog.tsx';
import { SpeakerAttributionDialog } from './SpeakerAttributionDialog.tsx';
import type { StructuredContent } from '@ojb/shared';

/**
 * Shape of the Meeting row this panel emits via onMeetingUpdated.
 *
 * AiEnhancements only READS id/title/structuredContent/preTranslate*/
//   inputSource/projectId. The remaining fields exist purely as
//   pass-through metadata that EditorRoute (and any other caller)
//   reads back off the updated row — the AI mutations don't change
//   them, but the API returns the full row so these fields ARE
//   present at runtime. They were missing here previously, which
//   silently allowed EditorRoute to drift from the schema (a field
//   rename in db/schema.ts would have only been caught at runtime).
//
//   Optional rather than required because future API responses might
//   omit fields we don't strictly need; the `?? fallback` pattern at
//   call sites handles undefined safely.
interface Meeting {
  id: string;
  title: string;
  structuredContent: Record<string, unknown>;
  /** Non-null when the current structured_content is a translation —
   *  presence enables the "Revert translation" button. */
  preTranslateContent?: Record<string, unknown> | null;
  preTranslateLanguage?: string | null;
  /** Used to gate the Speaker attribution check — transcript-only. */
  inputSource?: 'transcript' | 'ai_summary' | 'freeform';
  /** Used to gate the Consistency check — needs a project. */
  projectId?: string | null;
  // ── Pass-through fields. AiEnhancements does not modify these,
  // but the API response includes them, so callers (EditorRoute) can
  // read them off the emitted Meeting after onMeetingUpdated fires.
  meetingDate?: string;
  notesDate?: string;
  meetingFormat?: 'in_person' | 'video' | 'phone' | 'hybrid' | null;
  location?: string | null;
}

/** Keep in sync with the server's ToneRewritePreset in prompts.ts. */
type ToneRewritePreset = 'formal' | 'concise' | 'client_friendly' | 'executive';

interface TonePresetOption {
  value: ToneRewritePreset;
  label: string;
  hint: string;
}

const TONE_PRESETS: TonePresetOption[] = [
  {
    value: 'formal',
    label: 'More formal',
    hint: 'Elevate register; expand contractions; neutral third-person.',
  },
  {
    value: 'concise',
    label: 'More concise',
    hint: 'Cut 20–30% length without losing facts.',
  },
  {
    value: 'client_friendly',
    label: 'More client-friendly',
    hint: 'Soften jargon; expand acronyms on first use.',
  },
  {
    value: 'executive',
    label: 'More executive-level',
    hint: 'Lead with decisions + commitments; tighten discussion.',
  },
];

interface UsageMeta {
  costUsd: number;
  inputTokens: number;
  outputTokens: number;
  durationMs: number;
}

interface Props {
  meeting: Meeting;
  hasStructuredContent: boolean;
  onMeetingUpdated: (next: Meeting) => void;
  onTitleSuggested: (title: string) => void;
}

// English first so users can translate back to English from any target
// language. Order after that is roughly by OJB client volume.
const LANGUAGE_OPTIONS = [
  'English',
  'Spanish',
  'French',
  'German',
  'Portuguese',
  'Italian',
  'Mandarin Chinese',
  'Arabic',
  'Japanese',
  'Korean',
];

export function AiEnhancements({
  meeting,
  hasStructuredContent,
  onMeetingUpdated,
  onTitleSuggested,
}: Props) {
  const [translateOpen, setTranslateOpen] = useState(false);
  const [translateLanguage, setTranslateLanguage] = useState(LANGUAGE_OPTIONS[0]);
  const [customLanguage, setCustomLanguage] = useState('');
  const [suggestedTitle, setSuggestedTitle] = useState<string | null>(null);
  // 4e.iii — Confirmation modal before reverting a translation so a
  // mis-click doesn't silently discard edits the user made to the
  // translated version.
  const [confirmRevertOpen, setConfirmRevertOpen] = useState(false);

  const polishMutation = useMutation({
    mutationFn: async () =>
      api.post<{ meeting: Meeting; usage: UsageMeta }>(`/meetings/${meeting.id}/polish`),
    onSuccess: (data) => {
      onMeetingUpdated(data.meeting);
    },
  });

  const translateMutation = useMutation({
    mutationFn: async (targetLanguage: string) =>
      api.post<{ meeting: Meeting; usage: UsageMeta }>(`/meetings/${meeting.id}/translate`, {
        targetLanguage,
      }),
    onSuccess: (data) => {
      onMeetingUpdated(data.meeting);
      setTranslateOpen(false);
    },
  });

  /**
   * Revert the most recent translation — restores the stashed pre-translate
   * snapshot and clears the stash. Only shown when meeting.preTranslateContent
   * is present (server sets it on translate, nulls it on revert).
   */
  const revertTranslateMutation = useMutation({
    mutationFn: async () =>
      api.post<{ meeting: Meeting }>(`/meetings/${meeting.id}/revert-translate`),
    onSuccess: (data) => {
      onMeetingUpdated(data.meeting);
    },
  });

  const suggestTitleMutation = useMutation({
    mutationFn: async () =>
      api.post<{ title: string; usage: UsageMeta }>(
        `/meetings/${meeting.id}/suggest-title`,
      ),
    onSuccess: (data) => {
      setSuggestedTitle(data.title);
    },
  });

  // Batch 1 feature #2 — Executive TL;DR. Result is NOT persisted;
  // we show it in a modal with a Copy button. Users who want it in
  // the document can paste it into a freeform bullet or the email
  // summary.
  const [tldrOpen, setTldrOpen] = useState(false);
  const [tldrText, setTldrText] = useState<string | null>(null);
  const [tldrCopied, setTldrCopied] = useState(false);
  const tldrMutation = useMutation({
    mutationFn: async () =>
      api.post<{ summary: string; usage: UsageMeta }>(
        `/meetings/${meeting.id}/executive-summary`,
      ),
    onSuccess: (data) => {
      setTldrText(data.summary);
      setTldrCopied(false);
      setTldrOpen(true);
    },
  });

  // Batch 2 + 3 feature dialogs — each opens its own Modal with a
  // review UI. Dialogs receive the meeting and (when applicable)
  // report back with a new StructuredContent via onApply.
  const [extractOpen, setExtractOpen] = useState(false);
  const [ambiguityOpen, setAmbiguityOpen] = useState(false);
  const [consistencyOpen, setConsistencyOpen] = useState(false);
  const [glossaryOpen, setGlossaryOpen] = useState(false);
  const [speakerOpen, setSpeakerOpen] = useState(false);

  /**
   * Bridge between dialog onApply (which returns a new
   * StructuredContent) and the existing onMeetingUpdated prop
   * (which expects a full Meeting). Wraps the new content back into
   * the meeting row and bubbles up so the editor's draft + autosave
   * pick up the change.
   */
  const applyStructured = (next: StructuredContent) => {
    onMeetingUpdated({
      ...meeting,
      structuredContent: next as unknown as Record<string, unknown>,
    });
  };

  // Manual detect-followup trigger. The auto-after-finalize path
  // already lives in EditorRoute (maybeSuggestFollowup); this gives
  // the user the same detection on demand BEFORE they finalize, so
  // they can confirm Claude picked up a follow-up correctly while
  // they're still editing. Result renders as a small inline card
  // — actually creating the calendar invite still flows through
  // EditorRoute's FollowupSuggestionDialog after finalize so we
  // don't duplicate the Graph wiring here.
  interface FollowupDetectResponse {
    status: 'none' | 'vague' | 'specific';
    proposedDate?: string;
    proposedTitle?: string;
    proposedAttendees?: string[];
    evidenceQuote?: string;
  }
  const [followupResult, setFollowupResult] =
    useState<FollowupDetectResponse | null>(null);
  const followupMutation = useMutation({
    mutationFn: async () =>
      api.post<FollowupDetectResponse>(
        `/meetings/${meeting.id}/detect-followup`,
      ),
    onSuccess: (data) => {
      setFollowupResult(data);
    },
  });

  // Batch 1 feature #3 — Tone rewrite. Four presets; fires the same
  // mutation with a different `tone` body. Success replaces the
  // meeting's structured_content (same contract as Polish).
  const [toneMenuOpen, setToneMenuOpen] = useState(false);
  const toneMutation = useMutation({
    mutationFn: async (tone: ToneRewritePreset) =>
      api.post<{ meeting: Meeting; usage: UsageMeta }>(
        `/meetings/${meeting.id}/tone-rewrite`,
        { tone },
      ),
    onSuccess: (data) => {
      onMeetingUpdated(data.meeting);
      setToneMenuOpen(false);
    },
  });

  const disabled = !hasStructuredContent;
  // Accept any mutation-like with a `.error` field. Typing the
  // parameter as `ReturnType<typeof useMutation>` would force every
  // caller's narrow `UseMutationResult<TData, ..., TVariables>` to
  // be assignable to the unparameterized `UseMutationResult<unknown,
  // unknown, unknown, unknown>` — which fails on variance because
  // `mutate(variables)` is contravariant in TVariables. Picking just
  // the field we read sidesteps the variance trap entirely.
  const errorOf = (m: { error: unknown }) =>
    m.error instanceof ApiError ? m.error.message : m.error instanceof Error ? m.error.message : null;

  return (
    // Same .panel chrome family as the other left-pane panels
    // (bone bg + charcoal/8 + rounded-2xl + 20/22 padding). Header
    // is bordered-bottom so the "Operates on current minutes"
    // caption reads as a labeled header for the 12 cards below.
    <section className="rounded-2xl border border-charcoal/8 bg-bone px-[22px] py-5 shadow-sm">
      <div className="mb-4 flex items-baseline justify-between border-b border-charcoal/8 pb-3">
        <h2 className="text-[11px] font-bold uppercase tracking-[0.22em] text-pine">
          AI assist
        </h2>
        <span className="text-[10px] italic text-charcoal/55">
          Operates on current minutes
        </span>
      </div>

      <div className="grid grid-cols-1 gap-1.5">
        {/* Polish */}
        <button
          type="button"
          onClick={() => polishMutation.mutate()}
          disabled={disabled || polishMutation.isPending}
          className="flex items-center justify-between rounded-[10px] border border-charcoal/15 bg-cream px-3.5 py-2.5 text-left text-[12px] text-charcoal transition-colors hover:border-pine hover:bg-pine/[0.06] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-charcoal/15 disabled:hover:bg-cream"
        >
          <span>
            <span className="inline-flex items-center gap-1.5 font-medium">
              <Wand2 size={13} className="text-pine/70" />
              Polish
            </span>
            <span className="text-smoke"> - tighten tone and grammar</span>
          </span>
          {polishMutation.isPending && <span className="text-smoke">Polishing…</span>}
        </button>

        {/* Tone rewrites — 4 presets in a disclosure. Sibling of Polish
            because all five do full-document rewrites; Polish is the
            "neutral" option and these are the opinionated ones. */}
        <div className="space-y-2">
          <button
            type="button"
            onClick={() => setToneMenuOpen((v) => !v)}
            disabled={disabled || toneMutation.isPending}
            aria-expanded={toneMenuOpen}
            className="flex w-full items-center justify-between rounded-[10px] border border-charcoal/15 bg-cream px-3.5 py-2.5 text-left text-[12px] text-charcoal transition-colors hover:border-pine hover:bg-pine/[0.06] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-charcoal/15 disabled:hover:bg-cream"
          >
            <span>
              <span className="inline-flex items-center gap-1.5 font-medium">
                <Sliders size={13} className="text-pine/70" />
                Tone rewrite
              </span>
              <span className="text-smoke">
                {' '}- pick a stylistic target
              </span>
            </span>
            {toneMutation.isPending ? (
              <span className="text-smoke">Rewriting…</span>
            ) : (
              <span className="text-[10px] text-smoke">
                {toneMenuOpen ? '▴' : '▾'}
              </span>
            )}
          </button>
          {toneMenuOpen && !toneMutation.isPending && (
            <div className="space-y-1 rounded-[10px] border border-charcoal/10 bg-cream/60 p-2 text-xs">
              {TONE_PRESETS.map((p) => (
                <button
                  key={p.value}
                  type="button"
                  onClick={() => toneMutation.mutate(p.value)}
                  disabled={disabled || toneMutation.isPending}
                  className="block w-full rounded px-2 py-1.5 text-left transition-colors hover:bg-pine/5 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <div className="font-medium text-charcoal">{p.label}</div>
                  <div className="mt-0.5 text-[10px] leading-snug text-smoke">
                    {p.hint}
                  </div>
                </button>
              ))}
              <div className="mt-1 border-t border-smoke/20 pt-2 text-[10px] italic text-smoke">
                Rewrites the whole document. Direct quotes, ambiguity
                flags, attendees, and metadata stay intact.
              </div>
            </div>
          )}
        </div>

        {/* Executive TL;DR — generates a 3-5 sentence summary; shown in
            a modal with a Copy button. Not persisted on the meeting row. */}
        <button
          type="button"
          onClick={() => tldrMutation.mutate()}
          disabled={disabled || tldrMutation.isPending}
          className="flex items-center justify-between rounded-[10px] border border-charcoal/15 bg-cream px-3.5 py-2.5 text-left text-[12px] text-charcoal transition-colors hover:border-pine hover:bg-pine/[0.06] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-charcoal/15 disabled:hover:bg-cream"
        >
          <span>
            <span className="inline-flex items-center gap-1.5 font-medium">
              <AlignLeft size={13} className="text-pine/70" />
              TL;DR summary
            </span>
            <span className="text-smoke">
              {' '}- a 3–5 sentence skim-read
            </span>
          </span>
          {tldrMutation.isPending && (
            <span className="text-smoke">Summarizing…</span>
          )}
        </button>

        {/* Divider — "find + fix" actions below operate on specific
            kinds of problems rather than the whole doc. */}
        <div className="my-1 border-t border-charcoal/10" />

        {/* Extract missing action items (Batch 2 #1) */}
        <button
          type="button"
          onClick={() => setExtractOpen(true)}
          disabled={disabled}
          className="flex items-center justify-between rounded-[10px] border border-charcoal/15 bg-cream px-3.5 py-2.5 text-left text-[12px] text-charcoal transition-colors hover:border-pine hover:bg-pine/[0.06] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-charcoal/15 disabled:hover:bg-cream"
        >
          <span>
            <span className="inline-flex items-center gap-1.5 font-medium">
              <ListChecks size={13} className="text-pine/70" />
              Extract action items
            </span>
            <span className="text-smoke">
              {' '}- find commitments buried in prose
            </span>
          </span>
        </button>

        {/* Ambiguity resolver (Batch 2 #9) */}
        <button
          type="button"
          onClick={() => setAmbiguityOpen(true)}
          disabled={disabled}
          className="flex items-center justify-between rounded-[10px] border border-charcoal/15 bg-cream px-3.5 py-2.5 text-left text-[12px] text-charcoal transition-colors hover:border-pine hover:bg-pine/[0.06] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-charcoal/15 disabled:hover:bg-cream"
        >
          <span>
            <span className="inline-flex items-center gap-1.5 font-medium">
              <AlertTriangle size={13} className="text-pine/70" />
              Resolve ambiguities
            </span>
            <span className="text-smoke">
              {' '}- propose fixes for flagged bullets
            </span>
          </span>
        </button>

        {/* Consistency check (Batch 3 #4) — needs a project */}
        <button
          type="button"
          onClick={() => setConsistencyOpen(true)}
          disabled={disabled || !meeting.projectId}
          title={
            !meeting.projectId
              ? 'Requires a project to compare against.'
              : undefined
          }
          className="flex items-center justify-between rounded-[10px] border border-charcoal/15 bg-cream px-3.5 py-2.5 text-left text-[12px] text-charcoal transition-colors hover:border-pine hover:bg-pine/[0.06] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-charcoal/15 disabled:hover:bg-cream"
        >
          <span>
            <span className="inline-flex items-center gap-1.5 font-medium">
              <ScanSearch size={13} className="text-pine/70" />
              Consistency check
            </span>
            <span className="text-smoke">
              {' '}- vs prior project meetings
            </span>
          </span>
        </button>

        {/* Glossary harvester (Batch 3 #7) */}
        <button
          type="button"
          onClick={() => setGlossaryOpen(true)}
          disabled={disabled}
          className="flex items-center justify-between rounded-[10px] border border-charcoal/15 bg-cream px-3.5 py-2.5 text-left text-[12px] text-charcoal transition-colors hover:border-pine hover:bg-pine/[0.06] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-charcoal/15 disabled:hover:bg-cream"
        >
          <span>
            <span className="inline-flex items-center gap-1.5 font-medium">
              <BookOpen size={13} className="text-pine/70" />
              Harvest glossary
            </span>
            <span className="text-smoke">
              {' '}- propose new firm-glossary entries
            </span>
          </span>
        </button>

        {/* Speaker attribution check (Batch 3 #10) — transcripts only.
            When inputSource isn't 'transcript' the button is hidden
            entirely since it has nothing to act on. */}
        {meeting.inputSource === 'transcript' && (
          <button
            type="button"
            onClick={() => setSpeakerOpen(true)}
            disabled={disabled}
            className="flex items-center justify-between rounded-[10px] border border-charcoal/15 bg-cream px-3.5 py-2.5 text-left text-[12px] text-charcoal transition-colors hover:border-pine hover:bg-pine/[0.06] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-charcoal/15 disabled:hover:bg-cream"
          >
            <span>
              <span className="inline-flex items-center gap-1.5 font-medium">
                <Mic size={13} className="text-pine/70" />
                Check speakers
              </span>
              <span className="text-smoke">
                {' '}- verify attribution against attendees
              </span>
            </span>
          </button>
        )}

        {/* Detect follow-up — manual trigger for the same Claude
            detector that runs automatically after finalize. Useful
            for previewing whether the doc mentions a future
            meeting before committing to finalize. The actual
            calendar invite is still created via EditorRoute's
            FollowupSuggestionDialog after finalize. */}
        <div className="space-y-2">
          <button
            type="button"
            onClick={() => followupMutation.mutate()}
            disabled={disabled || followupMutation.isPending}
            className="flex w-full items-center justify-between rounded-[10px] border border-charcoal/15 bg-cream px-3.5 py-2.5 text-left text-[12px] text-charcoal transition-colors hover:border-pine hover:bg-pine/[0.06] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-charcoal/15 disabled:hover:bg-cream"
          >
            <span>
              <span className="inline-flex items-center gap-1.5 font-medium">
                <CalendarPlus size={13} className="text-pine/70" />
                Detect follow-up
              </span>
              <span className="text-smoke">
                {' '}- preview if a next meeting was discussed
              </span>
            </span>
            {followupMutation.isPending && (
              <span className="text-smoke">Checking…</span>
            )}
          </button>
          {followupResult && !followupMutation.isPending && (
            <div className="rounded-[10px] border border-charcoal/10 bg-cream p-3 text-[11px]">
              {followupResult.status === 'none' && (
                <div className="text-charcoal">
                  <span className="font-semibold text-pine">No follow-up detected.</span>{' '}
                  Claude didn't see a next meeting discussed in this content.
                </div>
              )}
              {followupResult.status === 'vague' && (
                <div className="text-charcoal">
                  <div>
                    <span className="font-semibold text-pine">Follow-up mentioned (no date).</span>{' '}
                    Participants discussed meeting again but didn't lock a
                    time.
                  </div>
                  {followupResult.evidenceQuote && (
                    <blockquote className="mt-1 border-l-2 border-pine/30 pl-2 italic text-charcoal/70">
                      "{followupResult.evidenceQuote}"
                    </blockquote>
                  )}
                  <div className="mt-1 text-[10px] italic text-smoke">
                    Finalize to schedule a follow-up invite via Outlook.
                  </div>
                </div>
              )}
              {followupResult.status === 'specific' && (
                <div className="text-charcoal">
                  <div>
                    <span className="font-semibold text-pine">
                      Specific follow-up detected.
                    </span>
                  </div>
                  <dl className="mt-1 grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5">
                    {followupResult.proposedTitle && (
                      <>
                        <dt className="font-semibold text-charcoal/60">Title</dt>
                        <dd>{followupResult.proposedTitle}</dd>
                      </>
                    )}
                    {followupResult.proposedDate && (
                      <>
                        <dt className="font-semibold text-charcoal/60">Date</dt>
                        <dd>{followupResult.proposedDate}</dd>
                      </>
                    )}
                    {followupResult.proposedAttendees &&
                      followupResult.proposedAttendees.length > 0 && (
                        <>
                          <dt className="font-semibold text-charcoal/60">Attendees</dt>
                          <dd>
                            {followupResult.proposedAttendees.join(', ')}
                          </dd>
                        </>
                      )}
                  </dl>
                  {followupResult.evidenceQuote && (
                    <blockquote className="mt-1 border-l-2 border-pine/30 pl-2 italic text-charcoal/70">
                      "{followupResult.evidenceQuote}"
                    </blockquote>
                  )}
                  <div className="mt-1 text-[10px] italic text-smoke">
                    Finalize to schedule the invite via Outlook.
                  </div>
                </div>
              )}
              <div className="mt-2 flex justify-end">
                <button
                  type="button"
                  onClick={() => setFollowupResult(null)}
                  className="text-[10px] text-charcoal/60 hover:text-pine"
                >
                  Dismiss
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Translate */}
        <div className="space-y-2">
          <button
            type="button"
            onClick={() => setTranslateOpen((v) => !v)}
            disabled={disabled || translateMutation.isPending}
            className="flex w-full items-center justify-between rounded-[10px] border border-charcoal/15 bg-cream px-3.5 py-2.5 text-left text-[12px] text-charcoal transition-colors hover:border-pine hover:bg-pine/[0.06] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-charcoal/15 disabled:hover:bg-cream"
          >
            <span>
              <span className="inline-flex items-center gap-1.5 font-medium">
                <Languages size={13} className="text-pine/70" />
                Translate
              </span>
              <span className="text-smoke"> - to another language</span>
            </span>
            {translateMutation.isPending && <span className="text-smoke">Translating…</span>}
          </button>

          {/* Revert translation — appears whenever a stashed pre-translation
              original exists on the meeting. Restores the EXACT wording
              from before translation (a full jsonb swap on the server).
              Any edits made to the translated version are lost; we warn
              about this explicitly, and Claude-based "Translate to
              English" remains available for edit-preserving round-trips. */}
          {meeting.preTranslateContent && !translateMutation.isPending && (
            // Mockup .ai-result: pine-soft + pine/30 + rounded-[10px]
            // + 10/14 padding. Used for AI inline result panels so
            // they read consistently across enhancements.
            <div className="rounded-[10px] border border-pine/30 bg-pine/[0.08] px-3.5 py-2.5 text-[12px]">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-pine">
                    Translated{meeting.preTranslateLanguage ? ` to ${meeting.preTranslateLanguage}` : ''}
                  </div>
                  <div className="mt-1 leading-snug text-[11px] text-smoke">
                    <b>Revert to original</b> restores the exact wording from
                    before translation - no words are dropped. Any edits you
                    made to the translated version will be discarded; use{' '}
                    <em>Translate → English</em> instead if you want to keep
                    those edits.
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setConfirmRevertOpen(true)}
                  disabled={revertTranslateMutation.isPending}
                  className="inline-flex shrink-0 items-center gap-1.5 rounded bg-pine px-3 py-1.5 text-[11px] font-medium text-cloud transition-colors hover:bg-pine/90 disabled:opacity-50"
                >
                  {revertTranslateMutation.isPending ? (
                    'Reverting…'
                  ) : (
                    <>
                      <RotateCcw size={12} />
                      Revert to original
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {translateOpen && !translateMutation.isPending && (
            <div className="rounded-[10px] border border-charcoal/10 bg-cream/60 p-3 text-xs">
              <label className="block">
                <span className="mb-1 block font-medium text-charcoal/80">Target language</span>
                <select
                  value={translateLanguage}
                  onChange={(e) => setTranslateLanguage(e.target.value)}
                  className="w-full rounded border border-smoke/40 bg-white px-2 py-1 text-xs focus:border-pine focus:outline-none"
                >
                  {LANGUAGE_OPTIONS.map((l) => (
                    <option key={l} value={l}>
                      {l}
                    </option>
                  ))}
                  <option value="__custom__">Other…</option>
                </select>
              </label>
              {translateLanguage === '__custom__' && (
                <input
                  {...NO_AUTOFILL}
                  type="text"
                  value={customLanguage}
                  onChange={(e) => setCustomLanguage(e.target.value)}
                  placeholder="e.g. Swedish"
                  className="mt-2 w-full rounded border border-smoke/40 bg-white px-2 py-1 text-xs focus:border-pine focus:outline-none"
                />
              )}
              <div className="mt-3 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setTranslateOpen(false)}
                  className="text-smoke hover:text-charcoal"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const lang =
                      translateLanguage === '__custom__' ? customLanguage.trim() : translateLanguage;
                    if (!lang) return;
                    translateMutation.mutate(lang);
                  }}
                  disabled={translateLanguage === '__custom__' && !customLanguage.trim()}
                  className="rounded bg-pine px-3 py-1 text-cloud hover:bg-pine/90 disabled:opacity-50"
                >
                  Translate
                </button>
              </div>
              <p className="mt-2 text-[10px] italic text-smoke">
                Warning: replaces the current structured content with the translation.
              </p>
            </div>
          )}
        </div>

        {/* Suggest title */}
        <div className="space-y-2">
          <button
            type="button"
            onClick={() => suggestTitleMutation.mutate()}
            disabled={disabled || suggestTitleMutation.isPending}
            className="flex items-center justify-between rounded-[10px] border border-charcoal/15 bg-cream px-3.5 py-2.5 text-left text-[12px] text-charcoal transition-colors hover:border-pine hover:bg-pine/[0.06] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-charcoal/15 disabled:hover:bg-cream"
          >
            <span>
              <span className="inline-flex items-center gap-1.5 font-medium">
                <Lightbulb size={13} className="text-pine/70" />
                Suggest title
              </span>
              <span className="text-smoke"> - Claude picks a concise one</span>
            </span>
            {suggestTitleMutation.isPending && <span className="text-smoke">Thinking…</span>}
          </button>

          {suggestedTitle && !suggestTitleMutation.isPending && (
            <div className="rounded-[10px] border border-pine/30 bg-pine/[0.08] p-3 text-xs">
              <div className="mb-2 font-medium text-pine">Suggested title</div>
              <div className="mb-3 italic text-charcoal">&ldquo;{suggestedTitle}&rdquo;</div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setSuggestedTitle(null)}
                  className="text-smoke hover:text-charcoal"
                >
                  Dismiss
                </button>
                <button
                  type="button"
                  onClick={() => {
                    onTitleSuggested(suggestedTitle);
                    setSuggestedTitle(null);
                  }}
                  className="rounded bg-pine px-3 py-1 text-cloud hover:bg-pine/90"
                >
                  Apply
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Error surface */}
      {(errorOf(polishMutation) ||
        errorOf(translateMutation) ||
        errorOf(revertTranslateMutation) ||
        errorOf(suggestTitleMutation) ||
        errorOf(toneMutation) ||
        errorOf(followupMutation) ||
        errorOf(tldrMutation)) && (
        <div className="mt-3 rounded-2xl border border-terra-cotta/30 bg-terra-cotta/10 px-3.5 py-2 text-[12px] text-terra-cotta">
          {errorOf(polishMutation) ||
            errorOf(translateMutation) ||
            errorOf(revertTranslateMutation) ||
            errorOf(suggestTitleMutation) ||
            errorOf(toneMutation) ||
            errorOf(followupMutation) ||
            errorOf(tldrMutation)}
        </div>
      )}

      {disabled && (
        <p className="mt-3 text-[10px] italic text-charcoal/55">
          Transform the meeting first to enable these actions.
        </p>
      )}

      {/* Batch 2 + 3 dialogs — each owns its own open/close state and
          mutation. On apply, dialogs that modify structuredContent
          call applyStructured() which bubbles up through
          onMeetingUpdated — the editor's draft + autosave handle
          persistence. */}
      <ExtractActionItemsDialog
        isOpen={extractOpen}
        onClose={() => setExtractOpen(false)}
        meeting={meeting}
        onApply={applyStructured}
      />
      <AmbiguityResolverDialog
        isOpen={ambiguityOpen}
        onClose={() => setAmbiguityOpen(false)}
        meeting={meeting}
        onApply={applyStructured}
      />
      <ConsistencyCheckDialog
        isOpen={consistencyOpen}
        onClose={() => setConsistencyOpen(false)}
        meetingId={meeting.id}
      />
      <GlossaryHarvesterDialog
        isOpen={glossaryOpen}
        onClose={() => setGlossaryOpen(false)}
        meetingId={meeting.id}
      />
      <SpeakerAttributionDialog
        isOpen={speakerOpen}
        onClose={() => setSpeakerOpen(false)}
        meeting={meeting}
        onApply={applyStructured}
      />

      {/* TL;DR modal — shows the generated executive summary with a
          Copy-to-clipboard button. The summary is NOT persisted on
          the meeting row; if the user wants it in the document they
          paste it into a bullet or the email summary. */}
      <Modal
        isOpen={tldrOpen}
        onClose={() => setTldrOpen(false)}
        size="md"
        ariaLabel="Executive TL;DR summary"
      >
        <ModalHeader
          title="Executive TL;DR"
          subtitle="A 3–5 sentence skim-ready paragraph generated from the current minutes. Copy + paste wherever useful."
          onClose={() => setTldrOpen(false)}
        />
        <ModalBody>
          {tldrText ? (
            <div className="rounded-2xl border border-pine/25 bg-pine/5 px-4 py-3 text-sm leading-relaxed text-charcoal">
              {tldrText}
            </div>
          ) : (
            <div className="text-sm italic text-charcoal/60">No summary generated.</div>
          )}
        </ModalBody>
        <ModalFooter className="flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={() => tldrMutation.mutate()}
            disabled={tldrMutation.isPending}
            className="rounded-full border border-charcoal/25 px-4 py-2 text-xs text-charcoal transition-colors hover:border-pine hover:text-pine disabled:opacity-50"
          >
            {tldrMutation.isPending ? 'Regenerating…' : 'Regenerate'}
          </button>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setTldrOpen(false)}
              className="rounded-full border border-charcoal/25 px-4 py-2 text-sm text-charcoal transition-colors hover:border-pine hover:text-pine"
            >
              Close
            </button>
            <button
              type="button"
              onClick={async () => {
                if (!tldrText) return;
                try {
                  await navigator.clipboard.writeText(tldrText);
                  setTldrCopied(true);
                  setTimeout(() => setTldrCopied(false), 1600);
                } catch {
                  // clipboard blocked — no graceful fallback needed; user
                  // can select-and-copy manually.
                }
              }}
              disabled={!tldrText}
              className="rounded-full bg-pine px-4 py-2 text-sm font-semibold text-cloud transition-colors hover:bg-pine/90 disabled:opacity-50"
            >
              {tldrCopied ? '✓ Copied' : 'Copy to clipboard'}
            </button>
          </div>
        </ModalFooter>
      </Modal>

      {/* Confirmation for Revert to original. Replaces the old
          window.confirm() so the dialog fits the Studio aesthetic
          and makes the "you'll lose edits" warning unmissable. */}
      <Modal
        isOpen={confirmRevertOpen}
        onClose={() => setConfirmRevertOpen(false)}
        size="md"
        ariaLabel="Confirm revert to original"
      >
        <ModalHeader
          title="Revert to the original?"
          subtitle="This restores the exact wording from before the translation ran."
          onClose={() => setConfirmRevertOpen(false)}
        />
        <ModalBody>
          <p className="text-sm text-charcoal/80">
            Any edits you made to the translated version will be
            discarded - the revert swaps the current structured
            content for the pre-translation snapshot verbatim.
          </p>
          <p className="mt-2 text-sm text-charcoal/80">
            If you want to keep those edits, cancel this and use{' '}
            <em>Translate &rarr; English</em> instead - Claude will
            render the translated text (with your edits) back into
            English.
          </p>
        </ModalBody>
        <ModalFooter className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => setConfirmRevertOpen(false)}
            className="rounded-full border border-charcoal/25 px-4 py-2 text-sm text-charcoal transition-colors hover:border-pine hover:text-pine"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => {
              setConfirmRevertOpen(false);
              revertTranslateMutation.mutate();
            }}
            className="rounded-full bg-pine px-4 py-2 text-sm font-semibold text-cloud transition-colors hover:bg-pine/90"
          >
            Revert
          </button>
        </ModalFooter>
      </Modal>
    </section>
  );
}
